import React, { useState, useEffect, FC } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// Shared UI Library Components
import { CustomButton } from '@ppmcore/seven-ppm-core-shared-components-react';

// Styles
import './specialty-specialisations-tab.scss';

// Store entities
import {
  fetchCompanySpecialtySpecialisations
} from '../../../../store/company/company.thunks';
import { getCompanyState } from '../../../../store/company/company.selectors';

// Components
import { SpecialtySpecialisationsCard } from '../specialty-specialisations-card/specialty-specialisations-card';
import {
  UpdateSpecialtySpecialisationsModal
} from '../../../../modals/update-specialty-specialisations-modal/update-specialty-specialisations-modal';
import {
  DeleteSpecialtySpecialisationsModal
} from '../../../../modals/delete-specialty-specialisations-modal/delete-specialty-specialisations-modal';

// Models
import { ISpecialtySpecialisations } from '../../../../interfaces/company.interfaces';

type TSpecialtySpecialisationsTabProps = {};

export const SpecialtySpecialisationsTab: FC<TSpecialtySpecialisationsTabProps> = (
  {}: TSpecialtySpecialisationsTabProps
) => {
  const [editOpen, setEditOpen] = useState<boolean>(false);
  const [deleteOpen, setDeleteOpen] = useState<boolean>(false);
  const [activeItem, setActiveItem] = useState<ISpecialtySpecialisations | null>(null);

  const {specialtySpecialisations} = useSelector(getCompanyState);

  const dispatch = useDispatch<any>();

  const firstList = specialtySpecialisations.filter((_, i) => i % 2 === 0);
  const secondList = specialtySpecialisations.filter((_, i) => i % 2 !== 0);

  const toggleDeleteModal = () => {
    if (deleteOpen) {
      setActiveItem(null);
    }
    setDeleteOpen(!deleteOpen);
  }

  const toggleEditModal = () => {
    if (editOpen) {
      setActiveItem(null);
    }
    setEditOpen(!editOpen);
  }

  const openDeleteSpecialtySpecialisationsModal = (info: ISpecialtySpecialisations) => {
    setActiveItem(info);
    toggleDeleteModal();
  }

  const openEditSpecialtySpecialisationsModal = (info: ISpecialtySpecialisations) => {
    setActiveItem(info);
    toggleEditModal();
  }

  const openAddSpecialtySpecialisationsModal = () => {
    toggleEditModal();
  }

  const loadSpecialtySpecialisations = async (): Promise<void> => {
    dispatch(fetchCompanySpecialtySpecialisations());
  }

  useEffect(() => {
    loadSpecialtySpecialisations();
  }, []);

  return (
    <div className="specialty-specialisations-tab">
      <UpdateSpecialtySpecialisationsModal open={editOpen}
                                           info={activeItem}
                                           onCancel={toggleEditModal}
                                           onSubmit={toggleEditModal}
      />
      <DeleteSpecialtySpecialisationsModal open={deleteOpen}
                                           info={activeItem}
                                           onCancel={toggleDeleteModal}
                                           onSubmit={toggleDeleteModal}
      />

      <div className="specialty-specialisations-tab--description business--description">
        <p>Describe your Consultants' skills and experience related to your services!</p>

        <p>In this section, you can specify the Specialties and Specializations of your Consultants. This is crucial for
          completing a Consultant's profile to indicate their expertise and professional focus areas. This information
          will
          also appear in the Consultant Addition Form and will be used in filters.</p>

        <p>It's important to note that a Consultant can have multiple Specialties with related Specializations, and one
          Specialty can encompass several Specializations. This flexibility ensures that Consultants can showcase their
          diverse skills and expertise accurately.</p>
      </div>
      <div className="specialty-specialisations-tab--body business--body-items">
        <div className="body-header">
          <CustomButton text={'Add'} size={'small'} type={'text'}
                        onClick={openAddSpecialtySpecialisationsModal}/>
        </div>
        <div className="body-list">
          <div className="body-list--column desktop-items">
            {
              firstList.map(info =>
                <SpecialtySpecialisationsCard
                  key={info.speciality.id}
                  info={info}
                  deleteItem={openDeleteSpecialtySpecialisationsModal}
                  editItem={openEditSpecialtySpecialisationsModal}
                />)
            }
          </div>
          <div className="body-list--column desktop-items">
            {
              secondList.map(info =>
                <SpecialtySpecialisationsCard
                  key={info.speciality.id}
                  info={info}
                  deleteItem={openDeleteSpecialtySpecialisationsModal}
                  editItem={openEditSpecialtySpecialisationsModal}
                />)
            }
          </div>
          <div className="body-list--column mobile-items">
            {
              specialtySpecialisations.map(info =>
                <SpecialtySpecialisationsCard
                  key={info.speciality.id}
                  info={info}
                  deleteItem={openDeleteSpecialtySpecialisationsModal}
                  editItem={openEditSpecialtySpecialisationsModal}
                />)
            }
          </div>
        </div>
      </div>
    </div>
  );
}
